import tinycolor from 'tinycolor2';
import { TGetColorStateDefinition, TParsedPalette } from './types';
import applyStates from './util/applyStates';
import applyStatesGetColor from './util/applyStatesGetColor';
import parsePalette from './util/parsePalette';
import { schemas } from './schemas';
import components from './components';
import { ColorModesScale, Theme } from '@theme-ui/css';
import merge from 'lodash-es/merge';

// Colors
const white = 'white';
export { parsePalette };
export function getSelectedColors(palette: TParsedPalette) {
  return palette.getColorStates(
    {
      backgroundColor: palette.background.paper,
      color: palette.text.primary,
    },
    {
      active: false,
      checked: false,
      default: false,
      disabled: false,
      focus: false,
      readonly: false,
    },
  );
}

export function getThemeColorsObject(theme: Theme): ColorModesScale {
  const colors = merge(
    {},
    {
      palette: {
        ...theme.palette,
        lightenColor: undefined,
        darkenColor: undefined,
        getContrastText: undefined,

        buildStateObject: undefined,
        getColor: undefined,
        getOneState: undefined,
        getStatesForColors: undefined,
        getStatesFromDefinition: undefined,
        getStatesFromPath: undefined,
      },
      accordion: {
        borderColor: theme.palette.border.section,
      },
      buttons: {
        accordion: {
          backgroundColor: theme.palette.primary.main,
          borderColor: theme.palette.border.section,
          color: theme.palette.primary.contrastText,
        },
        accordionPrimary: {
          backgroundColor: theme.palette.primary.main,
          borderColor: theme.palette.border.section,
          color: theme.palette.primary.contrastText,
        },
        close: {
          ...applyStatesGetColor(
            {
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.background.paper,
            },
            theme.palette,
          ),
          backgroundColor: 'transparent',
        },
        collapsibleAsidePanelTitle: {
          color: theme.palette.text.primary,
        },
        deletableInputButton: {
          ...applyStatesGetColor(
            {
              backgroundColor: 'transparent',
              color: theme.palette.secondary.main,
            },
            theme.palette,
          ),

          active: {
            color: theme.palette.darkenColor(theme.palette.secondary.main, 40),
          },
          disabled: {
            color: theme.palette.secondary.main,
          },
          focus: {
            color: theme.palette.darkenColor(theme.palette.secondary.main, 20),
          },
          hover: {
            color: theme.palette.darkenColor(theme.palette.secondary.main, 20),
          },
        },
        icon: {
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.text.icon,

          hover: {
            color: theme.palette.secondary.contrastText,
          },

          focus: {
            color: theme.palette.secondary.contrastText,
          },
        },
        iconAlert: {
          backgroundColor: 'transparent',
          color: theme.palette.text.icon,

          active: {
            backgroundColor: 'transparent',
            color: tinycolor('#e5c200').darken(10).toRgbString(),
          },

          focus: {
            backgroundColor: 'transparent',
            color: tinycolor('#e5c200').darken(5).toRgbString(),
          },

          hover: {
            backgroundColor: 'transparent',
            color: tinycolor('#e5c200').darken(5).toRgbString(),
          },
        },
        iconPrimary: {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,

          active: {
            color: theme.palette.primary.contrastText,
          },

          focus: {
            color: theme.palette.primary.contrastText,
          },

          hover: {
            color: theme.palette.primary.contrastText,
          },

          disabled: {
            color: theme.palette.gray['500'],
          },
        },
        iconToggled: {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,

          active: {
            color: theme.palette.primary.contrastText,
          },

          focus: {
            color: theme.palette.common.white,
          },

          hover: {
            color: theme.palette.common.white,
          },
        },
        iconOutline: {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.primary.main,
          borderColor: theme.palette.border.article,
        },
        iconOnly: {
          backgroundColor: 'transparent',
          color: theme.palette.primary.main,
          borderColor: theme.palette.background.paper,
          hover: {
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.darkenColor(
              theme.palette.background.paper,
              10,
            ),
          },
          active: {
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.darkenColor(
              theme.palette.background.paper,
              15,
            ),
          },
          focus: {
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.darkenColor(
              theme.palette.background.paper,
              10,
            ),
          },
        },
        imagePreview: {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderColor: theme.palette.border.article,
        },
        link: {
          backgroundColor: 'transparent',
          color: theme.palette.text.primary,
          borderColor: 'transparent',

          active: {
            color: theme.palette.text.primary,
          },

          focus: {
            color: theme.palette.text.primary,
          },

          hover: {
            color: theme.palette.text.primary,
          },
        },
        openTab: {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.primary.main,
          borderColor: theme.palette.border.field,
        },
        outline: {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderColor: theme.palette.border.field,
        },
        outlineDanger: {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderColor: theme.palette.border.field,

          active: {
            borderColor: theme.palette.darkenColor(
              theme.palette.error.dark,
              theme.palette.action.activeOpacity * 80,
            ),
            backgroundColor: theme.palette.darkenColor(
              theme.palette.error.dark,
              theme.palette.action.activeOpacity * 80,
            ),
            color: theme.palette.getContrastText(
              theme.palette.darkenColor(
                theme.palette.error.dark,
                theme.palette.action.activeOpacity * 80,
              ),
            ),
          },

          focus: {
            borderColor: theme.palette.darkenColor(
              theme.palette.error.dark,
              theme.palette.action.focusOpacity * 60,
            ),
            backgroundColor: theme.palette.darkenColor(
              theme.palette.error.dark,
              theme.palette.action.focusOpacity * 60,
            ),
            color: theme.palette.getContrastText(
              theme.palette.darkenColor(
                theme.palette.error.dark,
                theme.palette.action.focusOpacity * 60,
              ),
            ),
          },

          hover: {
            borderColor: theme.palette.darkenColor(
              theme.palette.error.dark,
              theme.palette.action.hoverOpacity * 10,
            ),
            backgroundColor: theme.palette.darkenColor(
              theme.palette.error.dark,
              theme.palette.action.hoverOpacity * 10,
            ),
            color: theme.palette.getContrastText(
              theme.palette.darkenColor(
                theme.palette.error.dark,
                theme.palette.action.hoverOpacity * 10,
              ),
            ),
          },
        },
        outlineWarning: {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderColor: theme.palette.border.field,

          active: {
            borderColor: theme.palette.warning.main,
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
          },

          focus: {
            borderColor: theme.palette.warning.main,
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
          },

          hover: {
            borderColor: theme.palette.warning.main,
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
          },
        },
        paper: {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderColor: theme.palette.background.paper,
        },
        primary: {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          borderColor: theme.palette.primary.main,
        },
        secondary: {
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
          borderColor: theme.palette.secondary.main,
        },
        tab: {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderColor: theme.palette.border.article,
          active: {
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.lightenColor(
              theme.palette.secondary.main,
              10,
            ),
          },
        },
        toggleIcon: {
          color: theme.palette.secondary.contrastText,
          backgroundColor: theme.palette.secondary.main,
          borderColor: theme.palette.border.field,

          danger: {
            backgroundColor: theme.palette.error.main,
            borderColor: theme.palette.border.field,
            color: theme.palette.error.contrastText,
          },
          toggled: {
            backgroundColor: theme.palette.primary.main,
            borderColor: theme.palette.border.field,
            color: theme.palette.primary.contrastText,
          },
        },
        transparentIcon: {
          backgroundColor: 'transparent',
          color: theme.palette.text.primary,

          hover: {
            color: theme.palette.primary.main,
          },

          focus: {
            color: theme.palette.primary.main,
          },
        },
      },
      components: components(theme),
      dropzone: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.border.ghost,
      },
      form: {
        backgroundColor: theme.palette.background.paper,

        fields: {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderColor: theme.palette.border.field,
        },

        radio: {
          backgroundColor: 'transparent',
          color: tinycolor(theme.palette.text.primary)
            .lighten(15)
            .toRgbString(),
          borderColor: theme.palette.text.primary,
          active: {
            backgroundColor: 'transparent',
            color: tinycolor(theme.palette.text.primary)
              .lighten(0)
              .toRgbString(),
          },
          checked: {
            backgroundColor: 'transparent',
            color: tinycolor(theme.palette.text.primary)
              .lighten(15)
              .toRgbString(),
          },
          disabled: {
            backgroundColor: 'transparent',
            color: tinycolor(theme.palette.text.primary)
              .lighten(55)
              .toRgbString(),
          },
          focus: {
            backgroundColor: 'transparent',
            color: tinycolor(theme.palette.text.primary)
              .lighten(10)
              .toRgbString(),
          },
          hover: {
            backgroundColor: 'transparent',
            color: tinycolor(theme.palette.text.primary)
              .lighten(7)
              .toRgbString(),
          },
          readonly: {
            color: tinycolor(theme.palette.text.primary)
              .lighten(38)
              .toRgbString(),
            backgroundColor: 'transparent',
          },
        },
      },
      modal: {
        borderColor: theme.palette.border.section,
        backgroundColor: theme.palette.background.paper,
      },
      notifications: {
        error: {
          backgroundColor: theme.palette.error.light,
          color: theme.palette.getContrastText(theme.palette.error.light),
          borderColor: theme.palette.error.main,
        },
        info: {
          backgroundColor: '#fdfdfd',
          color: theme.palette.text.primary,
          borderColor: theme.palette.primary.main,
        },
        success: {
          backgroundColor: theme.palette.success.light,
          color: theme.palette.getContrastText(theme.palette.success.light),
          borderColor: theme.palette.success.main,
        },
        warning: {
          backgroundColor: theme.palette.warning.light,
          color: theme.palette.getContrastText(theme.palette.warning.light),
          borderColor: theme.palette.warning.main,
        },
      },
      printView: {
        main: {
          backgroundColor: theme.palette.background.default,

          page: {
            backgroundColor: theme.palette.background.paper,
            borderColor: theme.palette.border.article,
          },
        },
        list: {
          table: {
            borderColor: theme.palette.border.section,
          },
          th: { backgroundColor: '#eee' },
          cell: {
            borderColor: theme.palette.border.article,
          },
        },
      },
      schemas,
      scrollbars: {
        bar: {
          color: theme.palette.darkenColor(
            theme.palette.background.default,
            50,
          ),
          backgroundColor: theme.palette.darkenColor(
            theme.palette.background.default,
            10,
          ),

          hover: {
            backgroundColor: theme.palette.darkenColor(
              theme.palette.background.default,
              10,
            ),
            color: theme.palette.darkenColor(theme.palette.border.article, 40),
          },

          active: {
            backgroundColor: theme.palette.darkenColor(
              theme.palette.background.default,
              10,
            ),
            color: theme.palette.darkenColor(theme.palette.border.article, 40),
          },
        } /*
      buttons: {
        backgroundColor: theme.palette.secondary.main,

        hover: {
          color: theme.palette.lightenColor(theme.palette.secondary.main, 10),
        },

        active: {
          color: theme.palette.lightenColor(theme.palette.secondary.main, 20),
        },
      }, */,
      },
      tab: {
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.border.section,
      },
      topBar: {
        backgroundColor: theme.palette.primary.main,
      },

      // Default ThemeUI colors
      accent: theme.palette.text.accent,
      background: theme.palette.background.default,
      iconWarning: 'rgb(246 222 87)',
      muted: theme.palette.text.disabled,
      primary: theme.palette.primary.main,
      secondary: theme.palette.secondary.main,
      text: theme.palette.text.primary,
      warning: theme.palette.warning.main,
      danger: theme.palette.error.main,
      link: theme.palette.text.accent,
      success: theme.palette.success.main,
      title: theme.palette.text.title,
      selected: theme.palette.getOneState(
        { backgroundColor: theme.palette.background.paper },
        'selected',
      ).backgroundColor,

      acquiredColor: '#2b2bd0',
      acquiredColorDisabled: '#7f7fd4',

      // Borders
      outlineColor: '#00a8f6',
      touchedBorderColor: 'orange',

      // Other colors
      favorite: '#e5c200',

      // States
      overdueDanger: 'rgb(189, 0, 3)',
      overdueWarning: 'hsl(50.53deg 100% 26.06%)',
      overdueWarningBackground: '#fffdd7',

      priorityNone: 'gray',
      priorityLow: theme.palette.success.main,
      priorityNormal: 'rgb(255 211 34)',
      priorityHigh: 'rgb(255 150 0)',
      priorityUrgent: 'red',

      // Common
      white,
    },
    theme.colors ?? {},
  );

  applyStates(
    [
      colors?.buttons as Record<string, TGetColorStateDefinition>,
      colors?.form as Record<string, TGetColorStateDefinition>,
      colors?.components as Record<string, TGetColorStateDefinition>,
    ],
    theme,
  );

  Object.entries(colors.components ?? {}).forEach(([name]) => {
    applyStates([colors.components[name]], theme);
  });

  return colors;
}
